import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader } from 'lucide-react';
import ContourViewport from './ContourViewport';
import ContourToolPanel from './ContourToolPanel';
import { newContourId, canDeleteVertex } from './fiSnowDigitizerGeometry';
import './FiSnowDigitizer.css';

const apiBase = (process.env.REACT_APP_API_ORIGIN ?? '') + '/api/truss-analysis/calculations';
const MAX_UNDO = 50;

export default function FiSnowDigitizer() {
  const undoStackRef = useRef([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const [skLevels, setSkLevels] = useState([2, 2.5, 2.75, 3, 3.5]);
  const [skColors, setSkColors] = useState({});
  const [mapImage, setMapImage] = useState(null);
  const [panelWidth, setPanelWidth] = useState(537);
  const [panelHeight, setPanelHeight] = useState(835);

  const [contours, setContours] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeSk, setActiveSk] = useState(2.5);
  const [lineKind, setLineKind] = useState('closed_isoline');
  const [toolMode, setToolMode] = useState('probe');
  const [draftPoints, setDraftPoints] = useState([]);
  const [probeHover, setProbeHover] = useState(null);
  const [probePins, setProbePins] = useState([]);
  const [probeScreen, setProbeScreen] = useState({ x: 0, y: 0 });

  const probeTimerRef = useRef(null);
  const probeSeqRef = useRef(0);

  const fetchProbe = useCallback(
    async (px, py) => {
      const seq = probeSeqRef.current + 1;
      probeSeqRef.current = seq;
      setProbeHover((prev) => ({ ...prev, loading: true, pixel_x: px, pixel_y: py }));
      try {
        const payload = { pixel_x: px, pixel_y: py };
        if (!readOnly) payload.contours = contours;
        const { data } = await axios.post(`${apiBase}/fi-snow-digitizer-probe/`, payload);
        if (seq !== probeSeqRef.current) return;
        setProbeHover({ ...data, loading: false, error: null });
      } catch (err) {
        if (seq !== probeSeqRef.current) return;
        setProbeHover({
          loading: false,
          error: err.response?.data?.error || err.message || 'Probe failed',
          pixel_x: px,
          pixel_y: py,
        });
      }
    },
    [contours, readOnly],
  );

  const scheduleProbe = useCallback(
    (px, py) => {
      if (probeTimerRef.current) clearTimeout(probeTimerRef.current);
      probeTimerRef.current = setTimeout(() => fetchProbe(px, py), 100);
    },
    [fetchProbe],
  );

  const addProbePin = useCallback(
    async (px, py) => {
      try {
        const payload = { pixel_x: px, pixel_y: py };
        if (!readOnly) payload.contours = contours;
        const { data } = await axios.post(`${apiBase}/fi-snow-digitizer-probe/`, payload);
        setProbePins((prev) => [...prev, { id: newContourId(), ...data }]);
      } catch (err) {
        setProbeHover({
          loading: false,
          error: err.response?.data?.error || err.message || 'Probe failed',
          pixel_x: px,
          pixel_y: py,
        });
      }
    },
    [contours, readOnly],
  );

  useEffect(
    () => () => {
      if (probeTimerRef.current) clearTimeout(probeTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (toolMode !== 'probe') {
      probeSeqRef.current += 1;
      if (probeTimerRef.current) clearTimeout(probeTimerRef.current);
    }
  }, [toolMode, contours]);

  const pushUndo = useCallback((snapshot) => {
    undoStackRef.current = [...undoStackRef.current.slice(-(MAX_UNDO - 1)), snapshot];
  }, []);

  const beginUndoableEdit = useCallback(() => {
    pushUndo({ contours, draftPoints, selectedId });
  }, [contours, draftPoints, selectedId, pushUndo]);

  const handleUndo = useCallback(() => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    const prev = stack[stack.length - 1];
    undoStackRef.current = stack.slice(0, -1);
    setContours(prev.contours);
    setDraftPoints(prev.draftPoints);
    setSelectedId(prev.selectedId);
  }, []);

  const finishDraft = useCallback(() => {
    if (draftPoints.length < 2) return;
    const closed = lineKind !== 'open';
    if (closed && draftPoints.length < 3) return;
    const role = lineKind === 'closed_zone' ? 'zone' : 'isoline';
    const contour = {
      id: newContourId(),
      sk_kN_m2: activeSk,
      closed,
      role,
      points: draftPoints.map((p) => [p[0], p[1]]),
    };
    setContours((prev) => [...prev, contour]);
    setDraftPoints([]);
    setSelectedId(contour.id);
    setSaveMessage(null);
  }, [draftPoints, lineKind, activeSk]);

  useEffect(() => {
    const onKey = (e) => {
      if (readOnly) return;
      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        handleUndo();
        return;
      }
      if (e.key === 'Escape') {
        setDraftPoints([]);
        return;
      }
      if (e.key === 'Enter' && toolMode === 'draw' && draftPoints.length >= 2) {
        e.preventDefault();
        finishDraft();
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedId && toolMode === 'select') {
          e.preventDefault();
          beginUndoableEdit();
          setContours((prev) => prev.filter((c) => c.id !== selectedId));
          setSelectedId(null);
        } else if (draftPoints.length) {
          e.preventDefault();
          setDraftPoints((prev) => prev.slice(0, -1));
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readOnly, toolMode, draftPoints, selectedId, handleUndo, finishDraft, beginUndoableEdit]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/fi-snow-digitizer-bootstrap/`);
        if (cancelled) return;
        const locked = Boolean(data.finalized || data.read_only);
        setReadOnly(locked);
        setSkLevels(data.sk_levels || [2, 2.5, 2.75, 3, 3.5]);
        setSkColors(data.sk_colors || {});
        setPanelWidth(data.panel_width_px || 537);
        setPanelHeight(data.panel_height_px || 835);
        setContours(data.contours || []);
        setToolMode(locked ? 'probe' : 'draw');
        if (data.map_image_b64) {
          setMapImage(`data:image/png;base64,${data.map_image_b64}`);
        }
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load digitizer');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    if (readOnly) return;
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      const { data } = await axios.post(`${apiBase}/fi-snow-digitizer-save/`, {
        contours,
        rebuild_grid: true,
      });
      const grid = data.grid;
      setSaveMessage(
        grid
          ? `Saved ${data.n_segments} segments · grid ${grid.n_cells} cells @ ${grid.step_m} m`
          : `Saved ${data.n_segments} contour segments`,
      );
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setToolModeSafe = useCallback(
    (mode) => {
      if (readOnly && mode !== 'probe') return;
      setToolMode(mode);
    },
    [readOnly],
  );

  if (loading) {
    return (
      <div className="fsd-loading">
        <Loader className="animate-spin" size={20} />
        Loading snow map…
      </div>
    );
  }

  return (
    <div className="fsd-studio">
      <ContourToolPanel
        readOnly={readOnly}
        skLevels={skLevels}
        skColors={skColors}
        activeSk={activeSk}
        setActiveSk={setActiveSk}
        lineKind={lineKind}
        setLineKind={setLineKind}
        toolMode={toolMode}
        setToolMode={setToolModeSafe}
        contours={contours}
        selectedId={selectedId}
        onSelectContour={setSelectedId}
        draftPoints={draftPoints}
        onFinishDraft={finishDraft}
        onCancelDraft={() => setDraftPoints([])}
        onDeleteSelected={() => {
          if (readOnly || !selectedId) return;
          beginUndoableEdit();
          setContours((prev) => prev.filter((c) => c.id !== selectedId));
          setSelectedId(null);
        }}
        onUndo={handleUndo}
        onSave={handleSave}
        saving={saving}
        saveMessage={saveMessage}
        error={error}
        probeHover={probeHover}
        probePins={probePins}
        onClearProbePins={() => setProbePins([])}
        onRemoveProbePin={(id) => setProbePins((prev) => prev.filter((p) => p.id !== id))}
      />
      <ContourViewport
        mapImage={mapImage}
        panelWidth={panelWidth}
        panelHeight={panelHeight}
        contours={contours}
        draftPoints={readOnly ? [] : draftPoints}
        draftClosed={lineKind !== 'open'}
        draftSk={activeSk}
        selectedId={readOnly ? null : selectedId}
        toolMode={readOnly ? 'probe' : toolMode}
        beginUndoableEdit={beginUndoableEdit}
        onAddDraftPoint={(pt) => {
          if (readOnly) return;
          setDraftPoints((prev) => [...prev, pt]);
        }}
        onFinishDraft={finishDraft}
        onSelectContour={setSelectedId}
        onMoveVertex={(contourId, vertexIndex, pt) => {
          if (readOnly) return;
          setContours((prev) =>
            prev.map((c) => {
              if (c.id !== contourId) return c;
              const points = c.points.map((p, i) => (i === vertexIndex ? [pt[0], pt[1]] : p));
              return { ...c, points };
            }),
          );
        }}
        onInsertVertex={(contourId, insertIndex, pt) => {
          if (readOnly) return;
          setContours((prev) =>
            prev.map((c) => {
              if (c.id !== contourId) return c;
              const points = [...c.points];
              points.splice(insertIndex, 0, [pt[0], pt[1]]);
              return { ...c, points };
            }),
          );
        }}
        onDeleteVertex={(contourId, vertexIndex) => {
          if (readOnly) return;
          setContours((prev) =>
            prev
              .map((c) => {
                if (c.id !== contourId) return c;
                if (!canDeleteVertex(c)) return c;
                return { ...c, points: c.points.filter((_, i) => i !== vertexIndex) };
              })
              .filter((c) => c.points.length >= 2),
          );
        }}
        onRemoveLastDraftPoint={() => setDraftPoints((prev) => prev.slice(0, -1))}
        probePins={probePins}
        probeHover={probeHover}
        probeScreen={probeScreen}
        onProbeMove={(px, py, screen) => {
          setProbeScreen(screen);
          scheduleProbe(px, py);
        }}
        onProbePin={addProbePin}
        onRemoveProbePin={(id) => setProbePins((prev) => prev.filter((p) => p.id !== id))}
      />
    </div>
  );
}
